import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { Link } from 'react-router-dom';
import ParticleGrid from './ParticleGrid';

const phrases = ["AI-powered", "blockchain-enabled", "environmentally sustainable"];

function App() {
  const [text, setText] = useState('');
  const [index, setIndex] = useState(0);
  const [visibleSections, setVisibleSections] = useState({
    typingLine: true,
    mainDescription: false,
    focusAreas: false,
    focusItems: false,
    activeProjects: false,
    projectItems: false,
    contact: false
  });
  const typingTimeout = useRef(null);

  useEffect(() => {
    const targetPhrase = phrases[index];

    if (text === targetPhrase) {
      typingTimeout.current = setTimeout(() => setText(t => t.slice(0, -1)), 2000);
    } else if (text.length < targetPhrase.length) {
      typingTimeout.current = setTimeout(() => setText(targetPhrase.slice(0, text.length + 1)), 200);
    }

    return () => clearTimeout(typingTimeout.current);
  }, [text, index]);

  useEffect(() => {
    if (text.length === 0) {
      setIndex((index + 1) % phrases.length);
    }
  }, [text,index]);

  // Staged content loading
  useEffect(() => {
    const timers = [];

    // Show main description after 2.5 seconds
    timers.push(setTimeout(() => {
      setVisibleSections(prev => ({ ...prev, mainDescription: true }));
    }, 2500));

    // Show focus areas header after 3.5 seconds
    timers.push(setTimeout(() => {
      setVisibleSections(prev => ({ ...prev, focusAreas: true }));
    }, 3500));

    // Show focus items after 4 seconds
    timers.push(setTimeout(() => {
      setVisibleSections(prev => ({ ...prev, focusItems: true }));
    }, 4000));

    // Show active projects header after 5 seconds
    timers.push(setTimeout(() => {
      setVisibleSections(prev => ({ ...prev, activeProjects: true }));
    }, 5000));

    // Show project items after 5.5 seconds
    timers.push(setTimeout(() => {
      setVisibleSections(prev => ({ ...prev, projectItems: true }));
    }, 5500));

    // Show contact info after 6.5 seconds
    timers.push(setTimeout(() => {
      setVisibleSections(prev => ({ ...prev, contact: true }));
    }, 6500));

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  return (
    <div className="App">
    <ParticleGrid />
      <header className="App-header" style={{ position: 'relative', zIndex: 1 }}>
        <div className="terminal-window">
          <div className="terminal-header">
            <span className="terminal-title">C12://ABOUT</span>
            <div className="terminal-controls">
              <span className="terminal-control">_</span>
              <span className="terminal-control">□</span>
              <span className="terminal-control">×</span>
            </div>
          </div>
          <div className="terminal-content">
            {visibleSections.typingLine && (
              <p className="terminal-line fade-in">
                <span className="terminal-prompt">$</span> Building solutions for a sustainable future{" "}
                <span className="terminal-highlight">{text}<span className="terminal-cursor">_</span></span>{" "}
              </p>
            )}

            {visibleSections.mainDescription && (
              <p className="terminal-line fade-in">
                <span className="terminal-prompt">$</span> C12 is a Web3 Product Studio building decentralized infrastructure and protocols for the next generation of the internet.
              </p>
            )}

            {visibleSections.focusAreas && (
              <p className="terminal-line fade-in">
                <span className="terminal-prompt">$</span> <span className="terminal-highlight">FOCUS AREAS:</span>
              </p>
            )}
            
            {visibleSections.focusItems && (
              <>
                <p className="terminal-line fade-in" style={{ animationDelay: '0.1s' }}>
                  <span className="terminal-prompt">&gt;</span> Blockchain Infrastructure
                </p>
                <p className="terminal-line fade-in" style={{ animationDelay: '0.2s' }}>
                  <span className="terminal-prompt">&gt;</span> Stablecoins and Tokenization
                </p>
                <p className="terminal-line fade-in" style={{ animationDelay: '0.3s' }}>
                  <span className="terminal-prompt">&gt;</span> Agentic Payment Infrastructure and Micropayments
                </p>
                <p className="terminal-line fade-in" style={{ animationDelay: '0.4s' }}>
                  <span className="terminal-prompt">&gt;</span> Privacy Tech & Post-quantum Cryptography
                </p>
                <p className="terminal-line fade-in" style={{ animationDelay: '0.5s' }}>
                  <span className="terminal-prompt">&gt;</span> Environmental Markets
                </p>
              </>
            )}

            {visibleSections.activeProjects && (
              <p className="terminal-line fade-in" style={{ marginTop: '1.5rem' }}>
                <span className="terminal-prompt">$</span> <span className="terminal-highlight">ACTIVE PROJECTS:</span>
              </p>
            )}
            
            {visibleSections.projectItems && (
              <>
                <p className="terminal-line fade-in" style={{ animationDelay: '0.1s' }}>
                  <span className="terminal-prompt">&gt;</span> Core contributor to{" "}
                  <a href="https://supernovanetwork.xyz/" className="terminal-link" target="_blank" rel="noopener noreferrer">
                    Supernova
                  </a>
                  {" "}blockchain
                </p>
                
                <p className="terminal-line fade-in" style={{ animationDelay: '0.2s' }}>
                  <span className="terminal-prompt">&gt;</span> Building{" "}
                  <a href="https://quantera.finance/" className="terminal-link" target="_blank" rel="noopener noreferrer">
                    Quantera
                  </a>
                  {" "}tokenization platform
                </p>
                
                <p className="terminal-line fade-in" style={{ animationDelay: '0.3s' }}>
                  <span className="terminal-prompt">&gt;</span> Pioneering sustainable business models with{" "}
                  <a href="https://modelcontextprotocol.io/introduction" className="terminal-link" target="_blank" rel="noopener noreferrer">MCP</a>
                  {" "}&{" "}
                  <a href="https://x402.org/" className="terminal-link" target="_blank" rel="noopener noreferrer">
                    x402
                  </a>
      
                </p>
              </>
            )}
            
            {visibleSections.contact && (
              <>
                <p className="terminal-line fade-in" style={{ marginTop: '1.5rem' }}>
                  <span className="terminal-prompt">$</span> Read our <Link to="/blog" className="terminal-link">blog</Link> for updates.
                </p>
                
                <p className="terminal-line fade-in" style={{ animationDelay: '0.1s' }}>
                  <span className="terminal-prompt">$</span> Contact:{" "}
                  <a href="mailto:info@c12.space" className="terminal-link">info@c12.space</a>
                </p>
              </>
            )}
            
            <div className="terminal-input" style={{ opacity: visibleSections.contact ? 1 : 0, transition: 'opacity 0.5s ease-in' }}>
              <Link to="/">
                <button className="terminal-button">[HOME]</button>
              </Link>
            </div>
          </div>
        </div>
      </header>
      <div className="footer">Carbon Twelve GmbH 2025 &copy;</div>
    </div>
  );
}

export default App;

